import { Component, OnInit } from '@angular/core';
import { UserDto } from '../_core/_model/users';
import { ApiService } from '../_core/_services/api.service';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotificationService } from '../_core/_services/notification.service';
import { AuthApiService } from '../_core/_services/auth-api.service';
import { AuthService } from '../_core/_services/auth.service';
import { FormatService } from '../_core/_services/format';
import {
  CreateCountryChangeRequestDto,
  CountryChangeProductType
} from '../_core/_model/country-change';
import { MiniCountryDto } from '../_core/_model/countries';
import { AccountDeletionEligibilityDto } from '../_core/_model/account-lifecycle';

declare const bootstrap: any;

@Component({
  selector: 'app-account',
  standalone: false,
  templateUrl: './account.html',
  styleUrl: './account.css',
})
export class Account implements OnInit {
  userData!: UserDto;
  loading = false;
  error = '';

  countries: MiniCountryDto[] = [];

  disable2faForm: FormGroup;
  countryChangeForm: FormGroup;
  deactivateAccountForm: FormGroup;
  deleteAccountForm: FormGroup;

  countryChangeSubmitted = false;
  submittingCountryChange = false;
  cancellingCountryChange = false;

  disable2faSubmitted = false;
  disabling2fa = false;

  recoveryCodes: string[] = [];
  recoveryRemaining: number | null = null;

  otpEnabled = true;

  downloadingAccountData = false;
  deactivatingAccount = false;
  deletingAccount = false;
  checkingDeletionEligibility = false;
  deletionEligibility: AccountDeletionEligibilityDto | null = null;

  deactivateAccountSubmitted = false;
  deleteAccountSubmitted = false;

  productTypes: CountryChangeProductType[] = ['Wallet'];

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private fn: FormatService,
    private authApi: AuthApiService,
    private auth: AuthService,
    private notify: NotificationService,
    private route: ActivatedRoute,
  ) {
    this.disable2faForm = this.fb.group({
      code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });

    this.countryChangeForm = this.fb.group({
      requestedCountryCode: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(2)]],
      productType: ['Wallet', Validators.required],
      reason: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(500)]],
    });

    this.deactivateAccountForm = this.fb.group({
      currentPassword: ['', Validators.required],
      confirmation: ['', [Validators.required, Validators.pattern(/^DEACTIVATE$/i)]],
      reason: ['', Validators.maxLength(500)],
      totpCode: [''],
      accountPin: [''],
    });

    this.deleteAccountForm = this.fb.group({
      currentPassword: ['', Validators.required],
      confirmation: ['', [Validators.required, Validators.pattern(/^DELETE$/)]],
      reason: ['', Validators.maxLength(500)],
      totpCode: [''],
      accountPin: [''],
    });
  }

  get lifecycleRequiresTotp(): boolean {
    return !!this.userData?.otp?.otpEnabled;
  }

  get lifecycleRequiresAccountPin(): boolean {
    return !this.lifecycleRequiresTotp && !!this.userData?.accountPin?.isSet;
  }

  get ccf() {
    return this.countryChangeForm.controls;
  }

  ngOnInit(): void {
    this.loadMain();
    this.getDetails();
  }

  loadMain() {
    this.loading = true;

    this.api.getMain().subscribe({
      next: (res) => {
        this.countries = res.data?.countries || [];
      },
      error: (e) => (this.error = e?.error?.Error?.Details ?? 'Failed to load main data'),
      complete: () => (this.loading = false),
    });
  }

  getDetails() {
    this.loading = true;

    this.api.getProfile().subscribe({
      next: (res) => {
        this.userData = res.data ?? null;
        console.log('User Data:', this.userData);
        this.otpEnabled = !!this.userData?.otp?.otpEnabled;
        this.removeCurrentCountryFromSelection();

        const tab = this.route.snapshot.queryParamMap.get('tab');
        if (tab) {
          setTimeout(() => this.activateTab(tab));
        }

        const jurisdiction = this.route.snapshot.queryParamMap.get('jurisdiction');

        if (jurisdiction) {
          this.countryChangeForm.patchValue({
            requestedCountryCode: jurisdiction.toUpperCase(),
            productType: 'Wallet',
            reason: `I need to complete compliance verification for ${jurisdiction.toUpperCase()} deposits.`
          });
        }
      },
      error: (e) => {
        const msg = e?.error?.Error?.Details ?? 'Failed to load profile';
        this.notify.showError(msg, 'User Profile');
      },
      complete: () => (this.loading = false),
    });
  }

  removeCurrentCountryFromSelection() {
    const currentCode = this.userData?.country?.default?.code?.toUpperCase();
    if (!currentCode) return;

    this.countries = this.countries.filter(c => c.code.toUpperCase() !== currentCode);
  }

  get currentCountryName(): string {
    return this.userData?.country?.default?.name ?? 'Not specified';
  }

  get currentCountryCode(): string {
    return this.userData?.country?.default?.code ?? '';
  }

  get complianceProfiles() {
    return this.userData?.country?.profiles ?? [];
  }

  get hasPendingCountryChangeRequest(): boolean {
    return !!this.userData?.country?.hasPendingChangeRequest;
  }

  get pendingRequest() {
    return this.userData?.country?.pendingRequest ?? null;
  }

  submitCountryChange() {
    this.countryChangeSubmitted = true;

    if (this.countryChangeForm.invalid) return;

    if (this.hasPendingCountryChangeRequest) {
      this.notify.showError(
        'You already have a pending country change request.',
        'Country Change'
      );
      return;
    }

    const requestedCountryCode = String(
      this.countryChangeForm.value.requestedCountryCode || ''
    ).toUpperCase();

    if (requestedCountryCode === this.currentCountryCode.toUpperCase()) {
      this.notify.showError(
        'Requested country cannot be the same as your current country.',
        'Country Change'
      );
      return;
    }

    const payload: CreateCountryChangeRequestDto = {
      requestedCountryCode,
      productType: this.countryChangeForm.value.productType,
      reason: this.countryChangeForm.value.reason
    };

    this.submittingCountryChange = true;

    this.api.submitCountryChangeRequest(payload).subscribe({
      next: (res) => {
        this.notify.showSuccess(
          res?.message || 'Country change request submitted successfully',
          'Success'
        );

        this.countryChangeSubmitted = false;
        this.countryChangeForm.reset({
          requestedCountryCode: '',
          productType: 'Wallet',
          reason: ''
        });

        this.getDetails();
      },
      error: (err) => {
        this.notify.showError(
          err?.error?.message || 'Failed to submit country change request',
          'Error'
        );
      },
      complete: () => this.submittingCountryChange = false
    });
  }

  cancelPendingCountryChange() {
    const requestId = this.pendingRequest?.id;
    if (!requestId) return;

    if (!confirm('Cancel this pending country change request?')) return;

    this.cancellingCountryChange = true;

    this.api.cancelCountryChangeRequest(requestId).subscribe({
      next: (res) => {
        this.notify.showSuccess(
          res?.message || 'Country change request cancelled.',
          'Country Change'
        );
        this.getDetails();
      },
      error: (err) => {
        this.notify.showError(
          err?.error?.message || 'Failed to cancel country change request.',
          'Country Change'
        );
      },
      complete: () => this.cancellingCountryChange = false
    });
  }

  activateTab(tab: string) {
    setTimeout(() => {
      const targetId = tab.startsWith('tab')
        ? tab
        : `tab${this.capitalize(tab)}`;

      const trigger = document.querySelector(
        `[data-bs-target="#${targetId}"]`
      ) as HTMLElement;

      if (trigger) {
        trigger.click();
      }
    });
  }

  capitalize(value: string): string {
    value = value.toLowerCase();
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  disable2fa() {
    this.disable2faSubmitted = true;
    if (this.disable2faForm.invalid) return;

    this.disabling2fa = true;
    const code = (this.disable2faForm.value.code ?? '').trim();

    this.authApi.totpDisable({ code }).subscribe({
      next: (res) => {
        this.disabling2fa = false;
        if (!res.success) {
          return this.notify.showError(res.message ?? 'Failed to disable 2FA', '2FA Setup');
        }

        this.notify.showSuccess('2FA disabled', '2FA Setup');
        if (this.userData) this.userData.otp!.otpEnabled = false;
      },
      error: (err) => {
        this.disabling2fa = false;
        this.notify.showError(err?.error?.message ?? 'Failed to disable 2FA', '2FA Setup');
      }
    });
  }

  openRecoveryModal() {
    const el = document.getElementById('recoveryCodesModal');
    if (!el) return;

    const m = bootstrap.Modal.getOrCreateInstance(el);
    m.show();

    this.authApi.recoveryStatus().subscribe({
      next: r => this.recoveryRemaining = r.data?.remaining ?? null,
      error: _ => { }
    });
  }

  generateRecoveryCodes() {
    this.authApi.recoveryGenerate().subscribe({
      next: (res) => {
        if (!res.success) return this.notify.showError(res.message ?? 'Failed to generate', 'Recovery Codes');
        this.recoveryCodes = res.data ?? [];
        this.recoveryRemaining = this.recoveryCodes.length;
        this.notify.showSuccess('Recovery codes generated', 'Recovery Codes');
      },
      error: (e) => this.notify.showError(e?.error?.message ?? 'Failed to generate', 'Recovery Codes')
    });
  }

  regenerateRecoveryCodes() {
    if (!confirm('Regenerating will invalidate old recovery codes. Continue?')) return;

    this.authApi.recoveryRegenerate().subscribe({
      next: (res) => {
        if (!res.success) return this.notify.showError(res.message ?? 'Failed to regenerate', 'Recovery Codes');
        this.recoveryCodes = res.data ?? [];
        this.recoveryRemaining = this.recoveryCodes.length;
        this.notify.showSuccess('Recovery codes regenerated', 'Recovery Codes');
        this.openRecoveryModal();
      },
      error: (e) => this.notify.showError(e?.error?.message ?? 'Failed to regenerate', 'Recovery Codes')
    });
  }

  async copyRecoveryCodes() {
    if (!this.recoveryCodes?.length) return;
    const text = this.recoveryCodes.join('\n');
    await navigator.clipboard.writeText(text);
    this.notify.showSuccess('Recovery codes copied', 'Recovery Codes');
  }

  downloadAccountData() {
    if (this.downloadingAccountData) return;

    this.downloadingAccountData = true;
    this.api.downloadAccountData().subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob) {
          this.notify.showError('The account export was empty.', 'Account Data');
          return;
        }

        const disposition = response.headers.get('content-disposition') || '';
        const fileName = this.getDownloadFileName(disposition);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);

        this.notify.showSuccess('Your account data was downloaded.', 'Account Data');
      },
      error: (err) => {
        this.downloadingAccountData = false;
        this.notify.showError(
          this.getApiErrorMessage(err, 'Failed to download account data.'),
          'Account Data'
        );
      },
      complete: () => this.downloadingAccountData = false
    });
  }

  prepareDeactivateAccount() {
    this.deactivateAccountSubmitted = false;
    this.deactivateAccountForm.reset({
      currentPassword: '',
      confirmation: '',
      reason: '',
      totpCode: '',
      accountPin: ''
    });
    this.configureLifecycleSecurityValidators(this.deactivateAccountForm);
  }

  deactivateAccount() {
    this.deactivateAccountSubmitted = true;
    if (this.deactivateAccountForm.invalid || this.deactivatingAccount) return;

    this.deactivatingAccount = true;
    this.api.deactivateAccount({
      currentPassword: this.deactivateAccountForm.value.currentPassword,
      confirmation: this.deactivateAccountForm.value.confirmation,
      reason: this.deactivateAccountForm.value.reason?.trim() || null,
      totpCode: this.deactivateAccountForm.value.totpCode?.trim() || null,
      accountPin: this.deactivateAccountForm.value.accountPin?.trim() || null
    }).subscribe({
      next: (res) => {
        this.hideModal('deactivateAccountModal');
        this.notify.showSuccess(
          res.message || 'Your account has been deactivated.',
          'Account Deactivated'
        );
        this.auth.logout('/auth/login').subscribe();
      },
      error: (err) => {
        this.deactivatingAccount = false;
        this.notify.showError(
          this.getApiErrorMessage(err, 'Failed to deactivate account.'),
          'Deactivate Account'
        );
      },
      complete: () => this.deactivatingAccount = false
    });
  }

  prepareDeleteAccount() {
    this.deleteAccountSubmitted = false;
    this.deleteAccountForm.reset({
      currentPassword: '',
      confirmation: '',
      reason: '',
      totpCode: '',
      accountPin: ''
    });
    this.configureLifecycleSecurityValidators(this.deleteAccountForm);
    this.loadDeletionEligibility();
  }

  loadDeletionEligibility() {
    if (this.checkingDeletionEligibility) return;

    this.checkingDeletionEligibility = true;
    this.deletionEligibility = null;

    this.api.getAccountDeletionEligibility().subscribe({
      next: (res) => this.deletionEligibility = res.data,
      error: (err) => {
        this.checkingDeletionEligibility = false;
        this.notify.showError(
          this.getApiErrorMessage(err, 'Failed to check whether the account can be deleted.'),
          'Delete Account'
        );
      },
      complete: () => this.checkingDeletionEligibility = false
    });
  }

  deleteAccount() {
    this.deleteAccountSubmitted = true;

    if (!this.deletionEligibility?.canDelete) {
      this.notify.showError(
        'Resolve the listed account items before deleting your account.',
        'Delete Account'
      );
      return;
    }

    if (this.deleteAccountForm.invalid || this.deletingAccount) return;

    this.deletingAccount = true;
    this.api.deleteAccount({
      currentPassword: this.deleteAccountForm.value.currentPassword,
      confirmation: this.deleteAccountForm.value.confirmation,
      reason: this.deleteAccountForm.value.reason?.trim() || null,
      totpCode: this.deleteAccountForm.value.totpCode?.trim() || null,
      accountPin: this.deleteAccountForm.value.accountPin?.trim() || null
    }).subscribe({
      next: (res) => {
        this.hideModal('deleteAccountModal');
        this.notify.showSuccess(
          res.message || 'Your account was permanently deleted.',
          'Account Deleted'
        );
        this.auth.logout('/auth/login').subscribe();
      },
      error: (err) => {
        this.deletingAccount = false;
        const eligibility = err?.error?.error?.details as AccountDeletionEligibilityDto | undefined;
        if (eligibility?.blockers) {
          this.deletionEligibility = eligibility;
        }

        this.notify.showError(
          this.getApiErrorMessage(err, 'Failed to delete account.'),
          'Delete Account'
        );
      },
      complete: () => this.deletingAccount = false
    });
  }

  private configureLifecycleSecurityValidators(form: FormGroup) {
    const totpCode = form.get('totpCode');
    const accountPin = form.get('accountPin');

    totpCode?.setValidators(
      this.lifecycleRequiresTotp
        ? [Validators.required, Validators.pattern(/^\d{6}$/)]
        : []
    );

    accountPin?.setValidators(
      this.lifecycleRequiresAccountPin
        ? [Validators.required, Validators.pattern(/^\d{6}$/)]
        : []
    );

    totpCode?.updateValueAndValidity({ emitEvent: false });
    accountPin?.updateValueAndValidity({ emitEvent: false });
  }

  private hideModal(id: string) {
    const element = document.getElementById(id);
    if (!element) return;
    bootstrap.Modal.getOrCreateInstance(element).hide();
  }

  private getDownloadFileName(contentDisposition: string): string {
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].replace(/["']/g, ''));

    const basicMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (basicMatch?.[1]) return basicMatch[1];

    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    return `swipfx-account-data-${stamp}.json`;
  }

  private getApiErrorMessage(err: any, fallback: string): string {
    const details = err?.error?.error?.details;
    if (typeof details === 'string' && details.trim()) return details;
    return err?.error?.message || fallback;
  }

  getKycApplicationStatusClass(status: string): string {
    return this.fn.getKycApplicationStatusClass(status);
  }
}
